/**
 * BackupPanel — Docker volumes snapshot + Postgres pg_dump + список архивов.
 *
 * Backend: /api/v1/devops/backup/{volumes,list,volume/create,volume/restore,delete,pg_dump}.
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Database,
  HardDrive,
  RefreshCw,
  Loader2,
  Archive,
  PackagePlus,
  RotateCcw,
  Trash2,
  ShieldAlert,
} from "lucide-react";
import {
  fetchDockerVolumes,
  fetchBackups,
  createVolumeBackup,
  restoreVolumeBackup,
  deleteBackup,
  pgDump,
  fetchNativeContainers,
  type DockerVolume,
  type BackupArchive,
  type NativeContainer,
} from "../../lib/api";
import { cn } from "../../lib/cn";

export function BackupPanel() {
  const [volumes, setVolumes] = useState<DockerVolume[]>([]);
  const [backups, setBackups] = useState<BackupArchive[]>([]);
  const [pgContainers, setPgContainers] = useState<NativeContainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    kind: "restore" | "delete";
    target: string;
    volume?: string;
    action: () => void;
  } | null>(null);

  async function load() {
    try {
      const [v, b, c] = await Promise.all([
        fetchDockerVolumes(),
        fetchBackups(),
        fetchNativeContainers({ all: false }),
      ]);
      setVolumes(v.items);
      setBackups(b.items);
      setPgContainers(c.items.filter((x) => /postgres|pgvector/.test(x.image)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function backup(volume: string) {
    setBusy(`backup:${volume}`);
    try {
      await createVolumeBackup(volume);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function restore(volume: string, backup: string, confirmed: boolean) {
    setBusy(`restore:${volume}`);
    try {
      await restoreVolumeBackup(volume, backup, { confirm: confirmed });
      setConfirmAction(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ")) {
        setConfirmAction({
          kind: "restore",
          target: backup,
          volume,
          action: () => restore(volume, backup, true),
        });
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  async function remove(name: string, confirmed: boolean) {
    setBusy(`del:${name}`);
    try {
      await deleteBackup(name, { confirm: confirmed });
      setConfirmAction(null);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes(" 409 ")) {
        setConfirmAction({
          kind: "delete",
          target: name,
          action: () => remove(name, true),
        });
      } else {
        setError(msg);
      }
    } finally {
      setBusy(null);
    }
  }

  async function pg(container: string, dbName: string) {
    setBusy(`pg:${container}`);
    try {
      await pgDump({ container, db: dbName });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={load}
          className="px-2 py-1.5 rounded border border-border/60 hover:bg-bg-subtle text-small text-fg-muted inline-flex items-center gap-1"
        >
          <RefreshCw className="size-3.5" /> Обновить
        </button>
        <span className="text-small text-fg-muted">
          {volumes.length} volumes · {backups.length} архивов · {pgContainers.length} postgres-контейнеров
        </span>
      </div>
      {error && <div className="rounded border border-danger/40 bg-danger/5 p-2 text-small text-danger">{error}</div>}
      {confirmAction && (
        <ConfirmDialog
          title={
            confirmAction.kind === "restore"
              ? `Restore перезапишет volume ${confirmAction.volume}`
              : `Удалить архив ${confirmAction.target}?`
          }
          onConfirm={confirmAction.action}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* Docker volumes */}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle flex items-center gap-1.5">
          <HardDrive className="size-3.5" />
          Docker volumes
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead>
              <tr className="text-left text-fg-subtle text-[11px] border-b border-border/30">
                <th className="px-3 py-2">NAME</th>
                <th className="px-3 py-2">DRIVER</th>
                <th className="px-3 py-2 text-right">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} className="p-4 text-center text-fg-muted"><Loader2 className="size-4 animate-spin inline"/></td></tr>
              ) : volumes.map((v) => (
                <tr key={v.name} className="border-b border-border/20 hover:bg-bg-subtle/30">
                  <td className="px-3 py-1.5 truncate max-w-[300px]" title={v.name}>{v.name}</td>
                  <td className="px-3 py-1.5 text-fg-muted">{v.driver}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => backup(v.name)}
                      disabled={busy === `backup:${v.name}`}
                      className="px-2 py-1 rounded text-[11px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-40 inline-flex items-center gap-1"
                    >
                      {busy === `backup:${v.name}` ? <Loader2 className="size-3 animate-spin" /> : <Archive className="size-3" />}
                      Snapshot
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Postgres containers */}
      {pgContainers.length > 0 && (
        <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
          <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle flex items-center gap-1.5">
            <Database className="size-3.5" />
            PostgreSQL контейнеры — pg_dumpall
          </div>
          <table className="w-full text-small font-mono">
            <tbody>
              {pgContainers.map((c) => (
                <PgRow key={c.name} container={c} busy={busy === `pg:${c.name}`} onDump={(db) => pg(c.name, db)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Архивы */}
      <div className="rounded-modal border border-border/60 bg-bg-card/40 overflow-hidden">
        <div className="px-3 py-2 border-b border-border/40 bg-bg-subtle/40 text-[11px] uppercase tracking-wider font-mono text-fg-subtle flex items-center gap-1.5">
          <Archive className="size-3.5" />
          Архивы /var/backups/aicores/
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-small font-mono">
            <thead>
              <tr className="text-left text-fg-subtle text-[11px] border-b border-border/30">
                <th className="px-3 py-2">NAME</th>
                <th className="px-3 py-2 text-right">SIZE</th>
                <th className="px-3 py-2">DATE</th>
                <th className="px-3 py-2 text-right">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {backups.length === 0 && (
                <tr><td colSpan={4} className="p-4 text-center text-fg-muted">архивов нет</td></tr>
              )}
              {backups.map((b) => {
                const volMatch = b.name.match(/^vol-([^-]+(?:-[^-]+)*?)-\d{8}-\d{6}\.tar\.gz$/);
                const volName = volMatch ? volMatch[1] : "";
                return (
                  <tr key={b.name} className="border-b border-border/20 hover:bg-bg-subtle/30">
                    <td className="px-3 py-1.5 truncate max-w-[400px]" title={b.name}>{b.name}</td>
                    <td className="px-3 py-1.5 text-right text-fg-muted tabular-nums">
                      {(b.size / 1024 / 1024).toFixed(1)} MB
                    </td>
                    <td className="px-3 py-1.5 text-fg-muted">
                      {new Date(b.mtime * 1000).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {volName && (
                        <button
                          type="button"
                          onClick={() => restore(volName, b.name, false)}
                          disabled={busy === `restore:${volName}`}
                          className="ml-1 px-2 py-1 rounded text-[11px] border border-warning/40 text-warning hover:bg-warning/10 disabled:opacity-40 inline-flex items-center gap-1"
                          title={`Восстановить в volume ${volName}`}
                        >
                          {busy === `restore:${volName}` ? <Loader2 className="size-3 animate-spin" /> : <RotateCcw className="size-3" />}
                          restore
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(b.name, false)}
                        disabled={busy === `del:${b.name}`}
                        className="ml-1 px-2 py-1 rounded text-[11px] border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-40 inline-flex items-center gap-1"
                      >
                        {busy === `del:${b.name}` ? <Loader2 className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
                        del
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PgRow({
  container,
  busy,
  onDump,
}: {
  container: NativeContainer;
  busy: boolean;
  onDump: (db: string) => void;
}) {
  const [db, setDb] = useState("");
  return (
    <tr className="border-b border-border/20 hover:bg-bg-subtle/30">
      <td className="px-3 py-1.5">{container.name}</td>
      <td className="px-3 py-1.5 text-fg-muted truncate max-w-[200px]">{container.image}</td>
      <td className="px-3 py-1.5">
        <input
          type="text"
          value={db}
          onChange={(e) => setDb(e.target.value)}
          placeholder="(пусто = pg_dumpall)"
          className="w-40 px-2 py-1 rounded border border-border/60 bg-bg-card text-small font-mono"
        />
      </td>
      <td className="px-3 py-1.5 text-right">
        <button
          type="button"
          onClick={() => onDump(db)}
          disabled={busy}
          className="px-2 py-1 rounded text-[11px] border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 disabled:opacity-40 inline-flex items-center gap-1"
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : <PackagePlus className="size-3" />}
          dump
        </button>
      </td>
    </tr>
  );
}

function ConfirmDialog({
  title,
  onConfirm,
  onCancel,
}: {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-warning/40 bg-warning/5 p-3 flex items-start gap-2"
    >
      <ShieldAlert className="size-4 mt-0.5 text-warning shrink-0" />
      <div className="flex-1">
        <div className="font-medium text-small">{title}</div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-md text-small font-medium text-white"
            style={{ backgroundImage: "var(--grad-cta)" }}
          >
            Подтверждаю
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-md text-small border border-border/60"
          >
            Отмена
          </button>
        </div>
      </div>
    </motion.div>
  );
}
